import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import { LayoutDashboard, MessageSquare, TrendingUp, Users } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalMessages: 0,
    todayMessages: 0,
    totalServices: 6,
    popularService: 'Floor Tiling',
  });
  const [recentMessages, setRecentMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/contact`);
      const messages = response.data;
      
      // Calculate today's messages
      const today = new Date().toDateString();
      const todayCount = messages.filter(msg => 
        new Date(msg.timestamp).toDateString() === today
      ).length;

      setStats({
        totalMessages: messages.length,
        todayMessages: todayCount,
        totalServices: 6,
        popularService: 'Floor Tiling',
      });

      setRecentMessages(messages.slice(0, 5));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const statCards = [
    { title: 'Total Messages', value: stats.totalMessages, icon: MessageSquare, color: 'blue' },
    { title: "Today's Messages", value: stats.todayMessages, icon: TrendingUp, color: 'green' },
    { title: 'Active Services', value: stats.totalServices, icon: LayoutDashboard, color: 'purple' },
    { title: 'Popular Service', value: stats.popularService, icon: Users, color: 'orange', isText: true },
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-AU', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <AdminLayout>
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8">Dashboard</h2>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            const colorClasses = {
              blue: 'bg-blue-100 text-blue-600',
              green: 'bg-green-100 text-green-600',
              purple: 'bg-purple-100 text-purple-600',
              orange: 'bg-orange-100 text-orange-600',
            };

            return (
              <div key={index} className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-lg ${colorClasses[stat.color]}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                </div>
                <h3 className="text-sm text-gray-600 mb-1">{stat.title}</h3>
                <p className={`text-2xl sm:text-3xl font-bold text-gray-900 ${stat.isText ? 'text-lg sm:text-xl' : ''}`}>
                  {stat.value}
                </p>
              </div>
            );
          })}
        </div>

        {/* Recent Messages */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">Recent Messages</h3>
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : recentMessages.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No messages yet</p>
          ) : (
            <div className="space-y-4">
              {recentMessages.map((message, index) => (
                <div key={index} className="border-l-4 border-blue-600 pl-4 py-2 hover:bg-gray-50 transition-colors">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{message.name}</h4>
                      <p className="text-sm text-gray-600 truncate">{message.email}</p>
                    </div>
                    <span className="text-xs sm:text-sm text-gray-500">{formatDate(message.timestamp)}</span>
                  </div>
                  <p className="text-sm text-gray-700 mt-2 line-clamp-2">{message.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;